import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Fingerprint, Compass, ShieldCheck, Feather, AlignCenter } from "lucide-react";
import { Logo, LaurelMark } from "@/components/Logo";

const HERO_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_victory-audit/artifacts/0hi2cle5_9BFDF74A-890F-405E-923A-EDAE2FF40DCC.png";
const PHILOSOPHY_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_victory-audit/artifacts/28j45fk7_Sharis%20Photo.png";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const AREAS_PREVIEW = [
  { icon: Fingerprint, name: "Identity", note: "Who you are apart from what you do." },
  { icon: Compass, name: "Decision-Making", note: "Intention over obligation, guilt, and urgency." },
  { icon: ShieldCheck, name: "Boundaries", note: "Limits that reflect what you value." },
  { icon: Feather, name: "Capacity", note: "Stewarding what you carry — not just carrying it." },
  { icon: AlignCenter, name: "Alignment", note: "A life that tells the truth about you." },
];

const MARQUEE_ITEMS = [
  "Identity before duty",
  "Leadership from within",
  "Boundaries as sacred alignment",
  "Sovereign living",
  "Your responsibilities aren't your identity",
];

function Header() {
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-ivory/85 border-b border-burgundy/10">
      <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-burgundy/80">
          <a href="#philosophy" data-testid="nav-philosophy-link" className="hover:text-burgundy transition-colors">Philosophy</a>
          <a href="#areas" data-testid="nav-areas-link" className="hover:text-burgundy transition-colors">The Five Areas</a>
        </nav>
        <button
          data-testid="nav-take-audit-btn"
          onClick={() => navigate("/audit")}
          className="rounded-full bg-burgundy text-champagne px-6 py-2.5 text-sm tracking-wide hover:bg-burgundy-deep transition-colors"
        >
          Start Your Check
        </button>
      </div>
    </header>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const navigate = useNavigate();

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
      <div className="absolute -top-32 -right-40 w-[34rem] h-[34rem] rounded-full bg-gold/15 blur-3xl" />
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="text-xs uppercase tracking-[0.35em] text-gold-muted mb-8"
            data-testid="hero-overline"
          >
            The Lady of Victory® Alignment Check
          </motion.p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight text-burgundy-deep">
            <span className="mask-line">
              <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
                Your responsibilities
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block">
                aren't your <em className="font-accent italic text-burgundy">identity.</em>
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-[#62686E] leading-relaxed"
            data-testid="hero-subtext"
          >
            You lead everyone else with excellence. This five-minute check reveals how
            intentionally you are leading yourself — across Identity, Decision-Making,
            Boundaries, Capacity, and Alignment — and names the one gap quietly costing you most.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <button
              data-testid="hero-begin-audit-btn"
              onClick={() => navigate("/audit")}
              className="group inline-flex items-center gap-3 rounded-full bg-burgundy px-8 py-4 text-champagne text-sm tracking-widest uppercase hover:bg-burgundy-deep transition-colors"
            >
              Begin Your Check
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <span className="text-xs uppercase tracking-[0.25em] text-[#62686E]">20 questions · 5 minutes</span>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 hidden lg:block"
        >
          <div className="gold-frame overflow-hidden" data-testid="hero-portrait">
            <motion.img
              src={HERO_IMG}
              alt="A poised woman leader in editorial light"
              style={{ y: imgY }}
              className="w-full h-[34rem] object-cover scale-110"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  const row = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="bg-burgundy-deep py-6 overflow-hidden border-y border-gold/20" data-testid="editorial-marquee">
      <div className="animate-marquee flex whitespace-nowrap w-max">
        {row.map((item, i) => (
          <span key={i} className="flex items-center text-champagne/90 font-serif text-xl sm:text-2xl italic px-8">
            {item}
            <LaurelMark className="w-5 h-5 mx-8" color="#A08C6E" />
          </span>
        ))}
      </div>
    </div>
  );
}

function Philosophy() {
  return (
    <section id="philosophy" className="py-28 sm:py-36">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <div className="gold-frame overflow-hidden">
            <img
              src={PHILOSOPHY_IMG}
              alt="An elegant executive woman in a warm interior, thoughtful"
              className="w-full h-[30rem] object-cover"
              data-testid="philosophy-portrait"
            />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold-muted mb-6">The Philosophy</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight text-burgundy-deep">
            You were never meant to disappear into the roles you carry so well.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#62686E] leading-relaxed max-w-xl">
            Mother. Executive. Founder. Pillar of every room you enter. The woman who can
            carry it all — and does. But capacity is not calling, and responsibility is not
            identity. Self-leadership begins the moment you lead yourself from who you are,
            not from what is demanded of you.
          </p>
          <p className="mt-4 text-base sm:text-lg text-[#62686E] leading-relaxed max-w-xl">
            This check is a mirror, not a verdict. In five minutes it shows you where you
            stand — and the single intentional step that starts closing the gap.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function Areas() {
  return (
    <section id="areas" className="py-28 bg-[#EEF0F2]">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold-muted mb-6">The Five Areas</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight text-burgundy-deep">
            One woman. Five dimensions of self-leadership.
          </h2>
        </motion.div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {AREAS_PREVIEW.map((a, i) => (
            <motion.div
              key={a.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.09 }}
              whileHover={{ y: -6 }}
              className="bg-ivory border border-gold/25 p-7 flex flex-col gap-4"
              data-testid={`area-card-${a.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <a.icon className="w-6 h-6 text-burgundy" strokeWidth={1.5} />
              <h3 className="font-serif text-xl text-burgundy-deep">{a.name}</h3>
              <p className="text-sm text-[#62686E] leading-relaxed">{a.note}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  const navigate = useNavigate();
  return (
    <section className="py-28 sm:py-36 bg-burgundy-deep text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
        <LaurelMark className="w-[36rem] h-[36rem] mx-auto mt-10" color="#EDE4D3" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9 }}
        className="relative max-w-3xl mx-auto px-6"
      >
        <p className="text-xs uppercase tracking-[0.35em] text-gold mb-6">Ready to close the gap?</p>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight text-champagne">
          Five honest minutes. One clear mirror. Your next intentional step.
        </h2>
        <button
          data-testid="cta-begin-audit-btn"
          onClick={() => navigate("/audit")}
          className="group mt-10 inline-flex items-center gap-3 rounded-full bg-gold px-10 py-4 text-burgundy-deep text-sm font-semibold tracking-widest uppercase hover:bg-champagne transition-colors"
        >
          Start Your Check
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
      </motion.div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-burgundy-deep border-t border-gold/15 py-10">
      <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
        <Logo dark />
        <p className="text-xs text-champagne/50 tracking-wide">
          © {new Date().getFullYear()} Lady of Victory®. Your responsibilities aren't your identity.
        </p>
        <Link to="/admin" data-testid="footer-admin-link" className="text-xs text-champagne/40 hover:text-champagne transition-colors tracking-widest uppercase">
          Admin
        </Link>
      </div>
    </footer>
  );
}

function BrandBanner() {
  return (
    <section className="py-24 bg-ivory" data-testid="brand-banner-section">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-4xl mx-auto px-6 text-center"
      >
        <p className="text-xs uppercase tracking-[0.35em] text-gold-muted mb-8">Lady of Victory® presents</p>
        <motion.div
          whileHover={{ scale: 1.015 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="gold-frame overflow-hidden"
        >
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_victory-audit/artifacts/2apmjusg_D135FC8D-3854-4600-BD3B-A07678E117AF.PNG"
            alt="Victory Within — a self-leadership installation for high-capacity ladies. From identity, not for identity."
            className="w-full object-cover"
            data-testid="brand-banner-image"
          />
        </motion.div>
        <p className="mt-8 font-serif italic text-xl sm:text-2xl text-burgundy-deep">
          From identity — never for identity.
        </p>
      </motion.div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="bg-ivory min-h-screen" data-testid="landing-page">
      <Header />
      <Hero />
      <Marquee />
      <Philosophy />
      <BrandBanner />
      <Areas />
      <FinalCta />
      <Footer />
    </div>
  );
}
